import { siteConfig } from "@acme/config";
import { toPlainText } from "@acme/mdx/lint";
import { NotFound } from "@acme/ui/not-found";
import { Skeleton } from "@acme/ui/skeleton";
import { CACHE_TAGS } from "@acme/utils/cache-tags";
import {
  ClientOnly,
  createFileRoute,
  ErrorComponent,
  Link,
  notFound,
} from "@tanstack/react-router";

import { lazy, Suspense, useEffect, useRef } from "react";
import { ArticleView } from "~/components/blog/article-view";
import { usePageEngagement } from "~/hooks/use-page-engagement";
import { taggedCacheHeaders } from "~/lib/cache-headers";
import { categoryLabel } from "~/lib/constants/blog-categories";
import { pickRelatedArticles } from "~/lib/related-articles";
import { seo } from "~/lib/seo";

const LikeButton = lazy(() => import("~/components/blog/like-button"));
const SocialShare = lazy(() => import("~/components/social-share"));

import { extractBlogFaqItems } from "~/lib/blog-faq";
import {
  $getAllPublicArticles,
  $getArticleBySlug,
  $viewArticle,
} from "~/lib/server/blog";
import {
  generateStructuredDataGraph,
  getBlogPostSchemas,
} from "~/lib/structured-data";
import { getBaseUrl } from "~/lib/utils";
import * as m from "~/paraglide/messages.js";
import { getLocale } from "~/paraglide/runtime.js";

const WORD_SPLIT_RE = /\s+/;

/**
 * Which locale the requested slug belongs to.
 *
 * The current locale wins when its slug matches. Scanning first looked
 * equivalent but is not: entries are checked in object key order, so a piece
 * whose locales share one slug — most of the portfolio, where "camping" is
 * "camping" in both — resolved to whichever key happened to come first. That
 * made the English page announce a Latvian URL in its structured data while
 * its own canonical said otherwise.
 *
 * The scan stays as the fallback for the genuinely useful case: arriving on a
 * slug that belongs to the other locale.
 */
function findSlugLocale(
  localeSlugs: Record<string, string>,
  slug: string | undefined
) {
  const currentLocale = getLocale();
  if (slug !== undefined && localeSlugs[currentLocale] === slug) {
    return currentLocale;
  }

  return (
    Object.entries(localeSlugs).find(
      ([, localeSlug]) => localeSlug === slug
    )?.[0] ?? currentLocale
  );
}

export const Route = createFileRoute("/(public)/blog/$articleId")({
  loader: async ({ params: { articleId } }) => {
    try {
      const locale = getLocale();
      // The listing query is already cached for the index; reusing it keeps
      // "keep reading" to zero extra round trips.
      const [article, all] = await Promise.all([
        $getArticleBySlug({ data: { slug: articleId, locale } }),
        $getAllPublicArticles({ data: { locale } }),
      ]);
      return {
        article,
        related: pickRelatedArticles(article, all),
      };
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message === "Article not found" ||
          error.message === "Article is not public")
      ) {
        throw notFound();
      }
      console.error("[loader] $getArticleBySlug failed:", error);
      throw error;
    }
  },
  headers: taggedCacheHeaders(CACHE_TAGS.blog),
  head: ({ loaderData }) => {
    // `head` still runs when the loader threw `notFound()`, with no loader
    // data. Without this branch every field below resolves to `undefined` and
    // the 404 advertises `<title>undefined | Lindevo</title>` plus a canonical
    // to the fabricated `/blog/undefined`.
    if (!loaderData?.article) {
      const notFoundSeo = seo({
        title: `${m.blog_not_found_title()} | ${siteConfig.title}`,
        path: "/blog",
        noindex: true,
      });
      return {
        meta: notFoundSeo.meta,
        links: notFoundSeo.links,
        scripts: [],
      };
    }

    const localeSlugs = loaderData?.article?.localeSlugs ?? {};
    const canonicalLocale = findSlugLocale(
      localeSlugs,
      loaderData?.article?.slug
    );
    const localePaths = Object.fromEntries(
      Object.entries(localeSlugs).map(([loc, slug]) => [loc, `/blog/${slug}`])
    );
    const canonicalPath =
      localePaths[canonicalLocale] ?? `/blog/${loaderData?.article?.slug}`;
    const seoData = seo({
      title: `${loaderData?.article?.title} | ${siteConfig.title}`,
      description: loaderData?.article?.description,
      keywords: siteConfig.keywords,
      image: loaderData?.article?.imageUrl,
      imageAlt: loaderData?.article?.imageAlt,
      author: loaderData?.article?.author?.name,
      type: "article",
      path: canonicalPath,
      localePaths,
      canonicalLocale,
    });
    // Counted on the prose a reader sees, not on component tags and code.
    const wordCount = loaderData?.article?.content
      ? toPlainText(loaderData.article.content)
          .split(WORD_SPLIT_RE)
          .filter(Boolean).length
      : 0;

    const structuredData = loaderData?.article?.title
      ? generateStructuredDataGraph(
          getBlogPostSchemas({
            title: loaderData.article.title,
            description: loaderData.article.description || "",
            image:
              loaderData.article.imageUrl || `${getBaseUrl()}/images/cover.png`,
            slug: loaderData.article.slug || "",
            // De-localized path plus the locale the slug resolved in: an
            // untranslated piece served under /lv/ is English content with
            // an English canonical, and its schema has to agree.
            locale: canonicalLocale,
            path: canonicalPath,
            // The first publication, not the draft's creation date.
            datePublished:
              (
                loaderData.article.publishedAt ?? loaderData.article.createdAt
              )?.toISOString() || new Date().toISOString(),
            dateModified:
              loaderData.article.updatedAt?.toISOString() ||
              new Date().toISOString(),
            wordCount,
            articleBody: loaderData.article.content ?? undefined,
            // The article's one filed subject, not `tags[0]` — tags are an open
            // list of stack names for the foot of a piece, so the first of them
            // would advertise "React" as the section a reader sees "Operations"
            // for on the card and in the listing filter.
            articleSection: categoryLabel(loaderData.article.category),
            // Only articles that actually render an FAQ section get FAQ markup.
            faqItems: extractBlogFaqItems(loaderData.article.content),
          })
        )
      : null;

    return {
      meta: seoData.meta,
      links: seoData.links,
      scripts: structuredData
        ? [
            {
              type: "application/ld+json",
              children: structuredData,
            },
          ]
        : [],
    };
  },
  component: RouteComponent,
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
  notFoundComponent: () => <NotFound>{m.blog_not_found()}</NotFound>,
});

function RouteComponent() {
  const { articleId } = Route.useParams();
  const { article, related } = Route.useLoaderData();
  const articleEntityId = article?.id;

  usePageEngagement({ pagePath: `/blog/${articleId}` });

  const hasViewedRef = useRef(false);
  useEffect(() => {
    if (!articleEntityId) {
      return;
    }
    if (hasViewedRef.current) {
      return;
    }
    hasViewedRef.current = true;

    const sessionKey = `viewed:${articleEntityId}`;
    if (sessionStorage.getItem(sessionKey)) {
      return;
    }
    sessionStorage.setItem(sessionKey, "1");

    $viewArticle({ data: { articleId: articleEntityId } }).catch((error) => {
      console.error(error);
    });
  }, [articleEntityId]);

  if (!article) {
    return null;
  }

  const likeFallback = <Skeleton className="h-10 w-[4.5rem] rounded-[7px]" />;
  const shareFallback = <Skeleton className="h-10 w-24 rounded-[7px]" />;

  return (
    <ArticleView
      actions={
        <div className="read-actions">
          <ClientOnly fallback={likeFallback}>
            <Suspense fallback={likeFallback}>
              <LikeButton article={article} />
            </Suspense>
          </ClientOnly>
          <ClientOnly fallback={shareFallback}>
            <Suspense fallback={shareFallback}>
              <SocialShare
                articleId={article.id}
                text={m.blog_share_via({
                  handle: siteConfig.author.handle,
                  title: article.title,
                })}
                url={`${siteConfig.url}/blog/${articleId}`}
              />
            </Suspense>
          </ClientOnly>
        </div>
      }
      article={article}
    >
      {related.length > 0 ? (
        <section className="related-section">
          <div className="related-heading">
            <h2>{m.blog_related_title()}</h2>
            <Link to="/blog">{m.blog_related_all()}</Link>
          </div>
          <ul className="related-links">
            {related.map((other) => (
              <li key={other.slug}>
                <Link params={{ articleId: other.slug }} to="/blog/$articleId">
                  <span className="small-note">
                    {categoryLabel(other.category)}
                  </span>
                  <h3>{other.title}</h3>
                  <span className="read-link">
                    {m.blog_read_link()} <span aria-hidden="true">↗</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </ArticleView>
  );
}
